import { getInitials, getOtherHouseholdMembers } from '@frontend/api-client';
import type { Client, Household } from '@frontend/api-types';
import { Avatar, Button, Text, XStack, YStack } from '@frontend/web-react';

type HouseholdManagementCardProps = {
  client?: Client;
  household?: Household;
  onInviteMember: () => void;
  onRemoveInvite: (inviteId: string, inviteName: string) => void;
  onRemoveMember: (memberId: string, memberName: string) => void;
};

export const HouseholdManagementCard = ({
  client,
  household,
  onInviteMember,
  onRemoveInvite,
  onRemoveMember,
}: HouseholdManagementCardProps) => {
  // Check if client is on household plan and is owner
  const isOnHouseholdPlan = client?.plan?.maxAccounts && client.plan.maxAccounts > 1;
  const isHouseholdOwner = client?.isHouseholdOwner;

  // Only show if on household plan and is owner
  if (!isOnHouseholdPlan || !isHouseholdOwner || !household) {
    return null;
  }

  const otherMembers = getOtherHouseholdMembers(client?.id, household);
  const hasOtherMembers = otherMembers.length > 0;
  const hasOpenInvites = household.openInvites && household.openInvites.length > 0;
  const hasAnyMembers = hasOtherMembers || hasOpenInvites;

  const openInvites = household.openInvites || [];

  return (
    <YStack className="bg-trueWhite border-1 border-borderPrimary rounded-lg p-md">
      <Text color="primary" type="p-md">
        Plan sharing
      </Text>

      {/* Existing members */}
      {hasOtherMembers ? (
        <YStack accessibilityLabel="Existing members" className="gap-sm mt-md">
          {otherMembers.map((member) => (
            <XStack className="items-center justify-between" key={member.id}>
              <XStack className="gap-sm">
                <Avatar
                  accessibilityLabel={`${member.name} avatar`}
                  initials={getInitials({
                    firstName: member.name,
                    lastName: member.lastName,
                    nickname: '',
                  })}
                  size="sm"
                />
                <Text color="primary" type="p-md">
                  {member.name}
                </Text>
              </XStack>
              <Button accessibilityLabel={`Remove ${member.name} from household`} label="Remove" onClick={() => onRemoveMember(member.id, member.name)} variant="secondary" />
            </XStack>
          ))}
        </YStack>
      ) : null}

      {/* Open invites */}
      {hasOpenInvites ? (
        <YStack accessibilityLabel="Open invites" className="gap-sm mt-md">
          {openInvites.map((invite) => (
            <XStack className="items-center justify-between" key={invite.id}>
              <XStack className="gap-sm">
                {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
                <div className="size-[24px] bg-neutral-300 rounded-full" />
                <Text color="tertiary" type="p-md">
                  {invite.firstName}
                </Text>
              </XStack>
              <Button
                accessibilityLabel={`Remove invite for ${invite.firstName}`}
                label="Remove invite"
                onClick={() => onRemoveInvite(invite.id, invite.firstName)}
                variant="secondary"
              />
            </XStack>
          ))}
        </YStack>
      ) : null}

      {!hasAnyMembers && (
        <XStack className="justify-end mt-md">
          <Button label="Add member" onClick={onInviteMember} variant="secondary" />
        </XStack>
      )}
    </YStack>
  );
};
